import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import SeoHead from '@/components/layout/SeoHead';
import CTABand from '@/components/sections/CTABand';
import { ArrowUpRight, BookOpen } from 'lucide-react';
import { POSTS as STATIC_POSTS, formatDate } from '@/blog/posts';
import { listBlogPosts } from '@/lib/api';

function normalizeDbPost(p) {
  return {
    slug: p.slug,
    title: p.title,
    date: (p.published_at || p.created_at || '').slice(0, 10),
    category: 'Resource',
    excerpt: p.meta_description || '',
    source: p.source || 'webhook',
  };
}

function mergePosts(staticPosts, dbPosts) {
  // DB posts take precedence on slug collision; otherwise interleave by date desc.
  const dbBySlug = new Map(dbPosts.map((p) => [p.slug, p]));
  const merged = [
    ...dbPosts,
    ...staticPosts.filter((p) => !dbBySlug.has(p.slug)),
  ];
  return merged.sort((a, b) => (b.date || '').localeCompare(a.date || ''));
}

export default function Blog() {
  const [dbPosts, setDbPosts] = useState([]);

  useEffect(() => {
    let cancelled = false;
    listBlogPosts({ limit: 100 })
      .then((res) => {
        if (cancelled) return;
        setDbPosts((res.posts || []).map(normalizeDbPost));
      })
      .catch((err) => {
        if (process.env.NODE_ENV !== 'production') {
          console.warn('[Blog] failed to load DB posts', err);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const posts = mergePosts(STATIC_POSTS, dbPosts);

  return (
    <>
      <SeoHead
        title="Cleanout Tips & Stories | Ohana Property Services"
        description="Practical guides for property cleanouts, junk removal, and estate transitions in Central Indiana, written by the team at Ohana Property Services."
        canonical="https://ohanapropertyexperts.com/blog"
      />

      <section className="bg-ohana-primary text-ohana-bg">
        <div className="container-ohana pt-24 pb-20 md:pt-32 md:pb-28">
          <p className="overline">From the Crew</p>
          <h1 className="mt-4 font-serif text-5xl md:text-6xl lg:text-7xl leading-[1.05] max-w-4xl">
            Cleanout tips, <span className="text-ohana-accent">straight from the truck.</span>
          </h1>
          <p className="mt-6 text-lg md:text-xl text-ohana-bg/75 max-w-2xl">
            Practical guides for homeowners, landlords, and investors across Central Indiana, written by the people doing the work.
          </p>
        </div>
      </section>

      <section className="section-pad bg-ohana-bg" data-testid="blog-index">
        <div className="container-ohana">
          {posts.length === 0 ? (
            <div className="rounded-2xl border-2 border-dashed border-ohana-border bg-white px-6 py-20 text-center">
              <span className="inline-grid h-16 w-16 place-items-center rounded-full bg-ohana-accent/10 text-ohana-accent">
                <BookOpen className="h-7 w-7" />
              </span>
              <p className="mt-6 font-serif text-2xl text-ohana-primary">First post coming soon.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-7">
              {posts.map((p, i) => {
                const featured = i === 0;
                return (
                  <Link
                    key={p.slug}
                    to={`/blog/${p.slug}`}
                    className={`group rounded-2xl border border-ohana-border bg-white p-7 md:p-8 transition-all hover:-translate-y-1 hover:shadow-xl hover:shadow-ohana-primary/5 ${
                      featured ? 'md:col-span-2' : ''
                    }`}
                    data-testid={`blog-card-${p.slug}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="inline-flex items-center gap-2 rounded-full bg-ohana-surface px-3 py-1 text-xs font-bold uppercase tracking-[0.18em] text-ohana-primary">
                        {p.category}
                      </span>
                      <ArrowUpRight className="h-5 w-5 text-ohana-primary transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
                    </div>
                    <h2
                      className={`mt-5 font-serif text-ohana-primary leading-tight ${
                        featured ? 'text-3xl md:text-4xl' : 'text-2xl md:text-3xl'
                      }`}
                    >
                      {p.title}
                    </h2>
                    <p className="mt-4 text-ohana-text-muted leading-relaxed">{p.excerpt}</p>
                    <p className="mt-6 text-xs font-bold uppercase tracking-[0.18em] text-ohana-text-muted">
                      {formatDate(p.date)}
                    </p>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <CTABand
        eyebrow="Skip the Reading"
        title="Want a quote instead?"
        subtitle="Send us photos and an address and we will reply same day with a transparent number."
      />
    </>
  );
}
